import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from "../../Components/UI/dialog";
import { Input } from "../../Components/UI/input";
import { Button } from "../../Components/UI/button";
import { Label } from "../../Components/UI/label";
import { Textarea } from "../../Components/UI/textarea";
import { TooltipProvider } from "../../Components/UI/tooltip";
import { Check, X, ArrowLeft, Upload, FileText, AlertTriangle, Lightbulb, Headset } from "lucide-react";
import { toast } from "sonner";
import { getMobileHeaderTitle } from "../../Utils/Utilities";
import { manageTktAPI } from "./TktAPI";
import apiService from "../../Services/APIService";

// Reusable Tailwind classes
const tktClasses = {
    spacing: "space-y-1.5",
    input: "h-11 text-sm transition-all duration-200 bg-white sahi-input",
    textarea: "min-h-[100px] text-sm transition-all duration-200 bg-white sahi-input resize-none",
    buttonPrimary: "sahi-primary-button",
    buttonSecondary: "sahi-secondary-button",
    gridCols1: "grid-cols-1",
    gridCols2: "grid-cols-1 sm:grid-cols-2",
    flexCol: "flex flex-col",
    flexRow: "flex flex-row",
    gap4: "gap-4",
    mb4: "mb-4",
    iconSize: "h-4 w-4",
    iconSizeMd: "h-5 w-5",
    iconSizeLg: "h-6 w-6"
};

const TktDialog = ({ isOpen = false, onClose, selectedFeedbackType = null, initialNotes = "" }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);

    // Form input values
    const [formData, setFormData] = useState({
        typeID: selectedFeedbackType || "",
        notes: initialNotes || ""
    });

    // Form validation
    const validateForm = () => {
        const validationRules = [
            {
                field: 'typeID',
                condition: formData.typeID != 1 && formData.typeID != 2 && formData.typeID != 3,
                message: 'Please select support type.',
                description: 'Support type is required.'
            },
            {
                field: 'notes',
                condition: !formData.notes.trim(),
                message: 'Please enter your notes.',
                description: 'Notes field is required.'
            },
            {
                field: 'notes',
                condition: formData.notes.trim().length < 10,
                message: 'Please provide more detailed.',
                description: 'Notes must be at least 10 characters long.'
            }
        ];

        for (const rule of validationRules) {
            if (rule.condition) {
                return {
                    isValid: false,
                    field: rule.field,
                    message: rule.message,
                    description: rule.description
                };
            }
        }

        return { isValid: true };
    };

    // Submit handler
    const handleSubmit = async () => {
        const validation = validateForm();
        if (!validation.isValid) {
            toast.error(validation.message, {
                description: validation.description,
            });
            return;
        }

        setIsLoading(true);
        try {
            // Get current URL
            const link = window.location.pathname;
            let tktFile = '';

            // Handle file upload if file is selected
            if (selectedFile) {
                try {
                    const uploadResult = await apiService.uploadFormFile(
                        selectedFile,
                        "SCTickets",
                        "image"
                    );

                    if (uploadResult.error) {
                        toast.error(uploadResult.message);
                        return;
                    }
                    tktFile = uploadResult.data.relativePath;
                } catch (uploadError) {
                    console.error('File upload error:', uploadError);
                    toast.error('Failed to upload file');
                    return;
                }
            }

            // Prepare feedback data for API
            const tktData = {
                typeID: formData.typeID,
                notes: formData.notes.trim(),
                link: link,
                tktFile: tktFile
            };

            // Submit feedback using API
            await manageTktAPI(tktData, 'ADD');

            // Show success message
            setShowSuccessMessage(true);

            // Auto close after 5 seconds
            setTimeout(() => {
                resetForm();
                setShowSuccessMessage(false);
                if (onClose) {
                    onClose();
                }
            }, 3000);
        } catch (error) {
            const errorMessage = error.isBusinessLogicError
                ? error.message
                : "Please try again or contact support if the issue persists.";
            toast.error(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    // Handle input field changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // Handle file upload
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedFile(file);
        }
    };

    // Reset form data
    const resetForm = () => {
        setFormData({
            typeID: selectedFeedbackType || "",
            notes: initialNotes || ""
        });
        setSelectedFile(null);
        setShowSuccessMessage(false);
    };

    // Update form data when selectedFeedbackType or initialNotes changes
    useEffect(() => {
        setFormData(prev => ({
            ...prev,
            typeID: selectedFeedbackType || prev.typeID,
            notes: initialNotes || prev.notes
        }));
    }, [selectedFeedbackType, initialNotes]);
    // Close dialog and reset states
    const handleClose = () => {
        resetForm();
        if (onClose) {
            onClose();
        }
    };

    // Support type options
    const tktTypes = [
        { value: 1, label: "Technical issue", icon: AlertTriangle },
        { value: 2, label: "Feature Suggestion", icon: Lightbulb },
        { value: 3, label: "Help Request", icon: Headset }
    ];

    const renderHeader = () => (
        <DialogHeader className="gap-0 px-3 sm:px-4 md:px-4">
            <div className="sm:hidden flex items-center justify-between w-full h-8">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                    <DialogClose tabIndex={-1} className="p-0 w-auto h-auto flex-shrink-0 flex justify-center">
                        <ArrowLeft className={tktClasses.iconSizeLg} />
                        <span className="sr-only">Back</span>
                    </DialogClose>
                    <DialogTitle className="text-lg font-medium">
                        <div className="truncate">
                            {getMobileHeaderTitle(
                                formData.typeID === 1 
                                    ? "Report Bug" 
                                    : formData.typeID === 3 
                                        ? "Help Request" 
                                        : "Suggest Idea"
                            )}
                        </div>
                    </DialogTitle>
                </div>
            </div>
            <DialogTitle className="hidden sm:block text-lg md:text-xl">
                {formData.typeID === 1 
                    ? "Report Bug" 
                    : formData.typeID === 3 
                        ? "Help Request" 
                        : "Suggest Idea"}
            </DialogTitle>

            <div className="hidden sm:flex items-center gap-2">
                <DialogClose
                    className="flex-shrink-0"
                    tabIndex={-1}
                    onClick={handleClose}
                    disabled={isLoading}
                >
                    <X className={tktClasses.iconSizeMd} />
                    <span className="sr-only">Close</span>
                </DialogClose>
            </div>
        </DialogHeader>
    );

    const renderActionButtons = () => (
        <div className="py-4 sm:px-0 sm:sticky sm:bottom-0 sm:bg-background sm:pb-3">
            <div className={`${tktClasses.flexCol} ${tktClasses.gap4} sm:flex-row sm:justify-start sm:gap-3 w-full`}>
                {/* Mobile Layout - Two buttons side by side */}
                <div className="flex sm:hidden w-full gap-3">
                    <Button
                        onClick={handleSubmit}
                        className={`${tktClasses.buttonPrimary} flex-1`}
                        disabled={isLoading}
                    >

                        <Check className={tktClasses.iconSize} />
                        Submit
                    </Button>
                    <Button
                        variant="secondary"
                        onClick={handleClose}
                        disabled={isLoading}
                        className="flex-1"
                    >
                        <X className={tktClasses.iconSize} />
                        Close
                    </Button>
                </div>

                {/* Desktop Layout - Submit first, then Close */}
                <Button
                    onClick={handleSubmit}
                    className={`${tktClasses.buttonPrimary} hidden sm:flex`}
                    disabled={isLoading}
                >

                    <Check className={tktClasses.iconSize} />
                    Submit
                </Button>

                {/* Desktop Layout - Close button */}
                <Button
                    variant="secondary"
                    onClick={handleClose}
                    disabled={isLoading}
                    className="hidden sm:flex"
                >
                    <X className={tktClasses.iconSizeMd} />
                    <span>Close</span>
                </Button>
            </div>
        </div>
    );

    return (
        <TooltipProvider>
            <Dialog
                open={isOpen}
                onOpenChange={(open) => {
                    if (!open && !isLoading) {
                        handleClose();
                    }
                }}
                preventOutsideClick={true}
            >
                <DialogContent className="flex flex-col w-full h-full max-h-screen overflow-hidden sm:w-[90vw] sm:max-w-[600px] sm:min-h-0 sm:max-h-[95vh] sm:h-auto !gap-0">

                    {renderHeader()}

                    <div className="px-4 pt-4 flex-1 scrollbar-no-arrows overflow-y-scroll sm:max-h-[85vh] sm:pb-0">
                        {showSuccessMessage ? (
                            <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
                                <div className="w-16 h-16 bg-sahi-green-light rounded-full flex items-center justify-center mb-4 animate-scale-in">
                                    <Check className="w-8 h-8 text-sahi-green animate-check-draw" />
                                </div>
                                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                                    Thank You!
                                </h3>
                                <p className="text-gray-600 mb-4 max-w-md">
                                    {formData.typeID === 1 
                                        ? "Thank you for reporting the issue." 
                                        : formData.typeID === 3 
                                            ? "Thank you for requesting help. We will contact you shortly!" 
                                            : "Thank you for sharing your suggestion!"}
                                </p>

                            </div>
                        ) : (
                            <div className="space-y-4">
                                <p className="text-sm text-gray-600 leading-relaxed mb-1">
                                    {formData.typeID === 1 
                                        ? "Please share the issue so we can resolve it." 
                                        : formData.typeID === 3 
                                            ? "Describe what you need help with and our support team will contact you." 
                                            : "How can we make SahiClub better for you?"}
                                </p>

                                {/* Notes Field - Full Width */}
                                <div className={tktClasses.spacing}>
                                    <Label htmlFor="notes" required={true}>
                                        {formData.typeID === 1 
                                            ? "Problem Description" 
                                            : formData.typeID === 3 
                                                ? "How can we help you?" 
                                                : "Please share your suggestion"}

                                    </Label>
                                    <Textarea
                                        id="notes"
                                        name="notes"
                                        value={formData.notes}
                                        onChange={handleInputChange}
                                        placeholder={
                                            formData.typeID === 1
                                                ? "e.g. App keeps logging me out after 5 minutes"
                                                : formData.typeID === 3
                                                    ? "e.g. I need help setting up my email integration or adding a new business"
                                                    : "e.g. Add dark mode for better night use"
                                        }
                                        className="min-h-[100px] text-sm transition-all duration-200 bg-white sahi-input resize-none"
                                        disabled={isLoading}
                                        maxLength={1000}
                                        rows={4}
                                    />
                                </div>
                                {/* File Upload */}
                                {formData.typeID === 1 && <div className={tktClasses.spacing}>
                                    <Label htmlFor="file" className="text-sm font-medium">
                                        Screenshot <span className="text-muted-foreground">(Optional)</span>
                                    </Label>
                                    <div className="relative">
                                        <Input
                                            id="file"
                                            type="file"
                                            accept=".jpg,.jpeg,.png,.gif"
                                            onChange={handleFileChange}
                                            className="hidden"
                                            disabled={isLoading}
                                        />
                                        <Button
                                            type="button"
                                            variant="outline"
                                            className="bg-sahi-white !px-3"
                                            onClick={() => document.getElementById("file").click()}
                                            disabled={isLoading}
                                        >
                                            {selectedFile ? (
                                                <div className="flex items-center gap-2 truncate w-full">
                                                    <FileText className={`${tktClasses.iconSize} text-sahi-green flex-shrink-0`} />
                                                    <span className="truncate text-sm flex-1">{selectedFile.name}</span>
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-4 w-4 p-0 flex-shrink-0 rounded-full"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setSelectedFile(null);
                                                            document.getElementById("file").value = "";
                                                        }}
                                                    >
                                                        <X className="h-3 w-3" />
                                                    </Button>
                                                </div>
                                            ) : (
                                                <div className="flex items-center gap-2 text-muted-foreground">
                                                    <Upload className={`${tktClasses.iconSize} `} />
                                                    <span className="text-sm ">
                                                        Upload Image
                                                    </span>
                                                </div>
                                            )}
                                        </Button>
                                    </div>
                                </div>}


                            </div>


                        )}
                        {!showSuccessMessage && renderActionButtons()}
                    </div>
                </DialogContent>
            </Dialog>
        </TooltipProvider>
    );
};

export default TktDialog;